import React from 'react';

import { Box, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';

import { TFunction } from 'i18next';
import { makeStyles } from 'tss-react/mui';

import AppAddPillButton from '../../../components/designe/buttons/app-add-pill-button/AppAddPillButton';
import AppAiPillButton from '../../../components/designe/buttons/app-ai-pill-button/AppAiPillButton';
import AppPlayPillButton from '../../../components/designe/buttons/app-play-pill-button/AppPlayPillButton';
import AppFeatureTooltipContent from '../../../components/designe/tooltips/AppFeatureTooltipContent';
import { Paths } from '../../../shared/paths';

const useStyles = makeStyles<Theme>()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 700,
    lineHeight: 1.375,
  },
  buttonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  buttonIcon: {
    flexShrink: 0,
    pointerEvents: 'none',
  },
  contentText: {
    lineHeight: 1.375,
  },
  itemContent: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    gap: theme.spacing(0.25),
  },
  itemTitle: {
    fontWeight: 700,
    lineHeight: 1.375,
  },
}));

interface ITrainingTooltipContentProps {
  t: TFunction;
}

export default function TrainingTooltipContent({
  t,
}: ITrainingTooltipContentProps) {
  const theme = useTheme();
  const { classes } = useStyles(theme);

  return (
    <AppFeatureTooltipContent
      footerText={t('onboarding.general.tooltipFooterText')}
      footerLinkText={t('onboarding.general.tooltipFooterLink')}
      footerLinkTo={Paths.settings.toFunctionalityView}>
      <Box className={classes.root}>
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle}>
            {t('common.actions')}
          </Typography>

          <Box className={classes.buttonRow}>
            <AppAiPillButton
              className={classes.buttonIcon}
              colorVariant="secondary"
              disabled
            />
            <Typography variant="text-sm" className={classes.contentText}>
              {t('onboarding.trainingPage.generateAi.content')}
            </Typography>
          </Box>

          <Box className={classes.buttonRow}>
            <AppPlayPillButton
              className={classes.buttonIcon}
              colorVariant="secondary"
              disabled
            />
            <Typography variant="text-sm" className={classes.contentText}>
              {t('onboarding.trainingPage.startNow.content')}
            </Typography>
          </Box>

          <Box className={classes.buttonRow}>
            <AppAddPillButton
              className={classes.buttonIcon}
              colorVariant="primary"
              disabled
            />
            <Typography variant="text-sm" className={classes.contentText}>
              {t('onboarding.trainingPage.createFuture.content')}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle}>
            {t('common.tabs')}
          </Typography>

          <Box className={classes.itemContent}>
            <Typography variant="text-sm" className={classes.itemTitle}>
              {t('onboarding.trainingPage.tabs.my.title')}
            </Typography>
            <Typography variant="text-sm" className={classes.contentText}>
              {t('onboarding.trainingPage.tabs.my.content')}
            </Typography>
          </Box>

          <Box className={classes.itemContent}>
            <Typography variant="text-sm" className={classes.itemTitle}>
              {t('onboarding.trainingPage.tabs.fromMe.title')}
            </Typography>
            <Typography variant="text-sm" className={classes.contentText}>
              {t('onboarding.trainingPage.tabs.fromMe.content')}
            </Typography>
          </Box>

          <Box className={classes.itemContent}>
            <Typography variant="text-sm" className={classes.itemTitle}>
              {t('onboarding.trainingPage.tabs.available.title')}
            </Typography>
            <Typography variant="text-sm" className={classes.contentText}>
              {t('onboarding.trainingPage.tabs.available.content')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </AppFeatureTooltipContent>
  );
}
